import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useCompany } from "#modules/company/view/useCompany";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { ROUTES } from "#shared/routes";
import { Avatar } from "#shared/view/components/Avatar";
import { Flex } from "#shared/view/components/Flex";
import { Button } from "#shared/view/designSystem/Button";
import {
  AssemblyWithChildren,
  JobStatusSchema,
  PartTypeSchema,
  PredictionModelTypeSchema,
  hasValidDataToPredict,
} from "shared";

import { getCompanyModelType } from "./helpers/getCompanyModelType";
import { QuoteRequestLineFormData } from "./types/QuoteRequestLineFormData";

const MAX_AVATARS_TO_DISPLAY: number = 5;

export const Cluster = ({
  quoteRequestLine,
  onClick,
  assembliesInBatch,
}: {
  quoteRequestLine: QuoteRequestLineDto;
  onClick?: () => void;
  assembliesInBatch: AssemblyWithChildren<QuoteRequestLineDto>[];
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { company } = useCompany();
  const modelType = getCompanyModelType(company);

  const { control } = useFormContext<QuoteRequestLineFormData>();
  const materialGrade = useWatch({ control, name: "materialGrade" });

  const shouldDisplaySimilarPartsButton =
    quoteRequestLine.predictionStatus !== JobStatusSchema.enum.PROCESSED &&
    modelType === PredictionModelTypeSchema.enum.CLUSTERING &&
    (!quoteRequestLine.predictionStatus ||
      !quoteRequestLine.cluster?.targetQuoteRequestLines ||
      quoteRequestLine.cluster.targetQuoteRequestLines.length === 0);

  const allThumbnailsAvatar = quoteRequestLine.cluster?.targetQuoteRequestLines
    .map((line) => line.part.urlPng3D ?? line.part.urlPng2D ?? "/images/no-preview.png")
    .filter((url) => !!url);

  const thumbnailsAvatar = allThumbnailsAvatar?.slice(0, MAX_AVATARS_TO_DISPLAY);

  const editCluster = () => {
    navigate(ROUTES.partnerQuoteRequestLineCluster(quoteRequestLine.id));
  };

  const hasSourceData = hasValidDataToPredict({
    materialGrade,
    isAssembly: quoteRequestLine.part.type === PartTypeSchema.enum.ASSEMBLY,
    children: assembliesInBatch.map((assembly) => assembly.quoteRequestLine),
  });

  if (!thumbnailsAvatar?.length) {
    if (shouldDisplaySimilarPartsButton) {
      return (
        <Flex h="100%" align={"center"} justify={"center"}>
          <Button onClick={editCluster} disabled={!hasSourceData}>
            {t("qr.clustering.button")}
          </Button>
        </Flex>
      );
    }

    return null;
  }

  return (
    <Flex h="100%" align={"center"} justify={"center"} mr="xl">
      <Avatar.Group onClick={onClick} style={{ cursor: "pointer" }} data-testid="avatar-group">
        {thumbnailsAvatar.map((img) => (
          <Avatar key={img} src={img} />
        ))}

        <MoreAvatarTag
          allThumbnailsAvatar={allThumbnailsAvatar}
          max_avatars_to_display={MAX_AVATARS_TO_DISPLAY}
        />
      </Avatar.Group>
    </Flex>
  );
};

const MoreAvatarTag = ({
  allThumbnailsAvatar,
  max_avatars_to_display = MAX_AVATARS_TO_DISPLAY,
}: {
  allThumbnailsAvatar: string[] | undefined;
  max_avatars_to_display: number;
}) => {
  const avatarLeftoversCount = !allThumbnailsAvatar?.length
    ? 0
    : allThumbnailsAvatar.length - max_avatars_to_display;

  const shouldDisplayMoreAvatarTag = avatarLeftoversCount > 0;

  if (!shouldDisplayMoreAvatarTag) {
    return null;
  }

  return <Avatar>+{avatarLeftoversCount}</Avatar>;
};
